import "./CookieBanner.scss";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const CONSENT_KEY = "covaltech_cookie_consent";

type ConsentValue = "accepted" | "declined";

function updateGtagConsent(value: ConsentValue) {
  const granted = value === "accepted" ? "granted" : "denied";
  if (typeof window !== "undefined" && "gtag" in window) {
    (window as Window & { gtag: (...args: unknown[]) => void }).gtag(
      "consent",
      "update",
      {
        ad_storage: granted,
        analytics_storage: granted,
        ad_user_data: granted,
        ad_personalization: granted,
      },
    );
  }
}

const CookieBanner = () => {
  const { t } = useTranslation();
  // Read once at mount: the banner shows until a choice has been stored.
  const [visible, setVisible] = useState(
    () => !localStorage.getItem(CONSENT_KEY),
  );

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (stored) {
      updateGtagConsent(stored as ConsentValue);
    }
  }, []);

  function handleAccept() {
    localStorage.setItem(CONSENT_KEY, "accepted");
    updateGtagConsent("accepted");
    setVisible(false);
  }

  function handleDecline() {
    localStorage.setItem(CONSENT_KEY, "declined");
    updateGtagConsent("declined");
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div
      className="cookieBanner"
      role="dialog"
      aria-live="polite"
      aria-label={t("cookie.aria_label")}
    >
      <p className="cookieBanner__text">
        {t("cookie.message")}{" "}
        <a
          className="cookieBanner__link"
          href={t("cookie.policy_url")}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("cookie.policy_label")}
        </a>
        .
      </p>
      <div className="cookieBanner__actions">
        <button
          className="cookieBanner__btn cookieBanner__btn--decline"
          onClick={handleDecline}
        >
          {t("cookie.decline")}
        </button>
        <button
          className="cookieBanner__btn cookieBanner__btn--accept"
          onClick={handleAccept}
        >
          {t("cookie.accept")}
        </button>
      </div>
    </div>
  );
};

export default CookieBanner;
